import React from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Contexts
import { LanguageProvider } from './context/LanguageContext';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { UserProvider } from './context/UserContext';

// Layout Components
import Header from './components/Header';
import Footer from './components/Footer';
import SpinWheel from './components/SpinWheel';
import WhatsAppButton from './components/WhatsAppButton';
import ScrollToTop from './components/ScrollToTop';

// Public Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import SupportPage from './pages/SupportPage';
import PricingPage from './pages/PricingPage';
import CoursesPage from './pages/CoursesPage';
import NewsPage from './pages/NewsPage';
import ReferralPage from './pages/ReferralPage';
import FAQPage from './pages/FAQPage';
import LoginPage from './pages/LoginPage';
import SellPage from './pages/SellPage';
import VendorDashboard from './pages/VendorDashboard';
import AffiliateDashboard from './pages/AffiliateDashboard';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import ChiSiamoPage from './pages/ChiSiamoPage';
import QualitaPage from './pages/QualitaPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminSuppliers from './pages/admin/AdminSuppliers';
import AdminImport from './pages/admin/AdminImport';
import AdminTickets from './pages/admin/AdminTickets';
import AdminPricing from './pages/admin/AdminPricing';
import AdminDiscovery from './pages/admin/AdminDiscovery';
import AdminAutoUpdate from './pages/admin/AdminAutoUpdate';
import AdminAffiliates from './pages/admin/AdminAffiliates';
import AdminVendors from './pages/admin/AdminVendors';
import AdminTrending from './pages/admin/AdminTrending';
import AdminCategories from './pages/admin/AdminCategories';

// Public Layout
const PublicLayout = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <UserProvider>
          <LanguageProvider>
            <CartProvider>
              <Routes>
                {/* Public Routes */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/:id" element={<ProductDetailPage />} />
                  <Route path="/product/:id" element={<ProductDetailPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order-success" element={<OrderSuccessPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/partner" element={<PricingPage />} />
                  <Route path="/courses" element={<CoursesPage />} />
                  <Route path="/academy" element={<CoursesPage />} />
                  <Route path="/news" element={<NewsPage />} />
                  <Route path="/magazine" element={<NewsPage />} />
                  <Route path="/referral" element={<ReferralPage />} />
                  <Route path="/invite" element={<ReferralPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/normative" element={<FAQPage />} />
                  <Route path="/sell" element={<SellPage />} />
                  <Route path="/vendi" element={<SellPage />} />
                  <Route path="/vendor/dashboard" element={<VendorDashboard />} />
                  <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsConditionsPage />} />
                  <Route path="/termini" element={<TermsConditionsPage />} />
                  <Route path="/terms-conditions" element={<TermsConditionsPage />} />
                  <Route path="/chi-siamo" element={<ChiSiamoPage />} />
                  <Route path="/about" element={<ChiSiamoPage />} />
                  <Route path="/qualita" element={<QualitaPage />} />
                  <Route path="/quality" element={<QualitaPage />} />
                </Route>

                {/* Auth Route (no header/footer) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<LoginPage />} />
                <Route path="/vendor/login" element={<LoginPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/forgot-password" element={<ResetPasswordPage />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="trending" element={<AdminTrending />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="affiliates" element={<AdminAffiliates />} />
                  <Route path="vendors" element={<AdminVendors />} />
                  <Route path="suppliers" element={<AdminSuppliers />} />
                  <Route path="import" element={<AdminImport />} />
                  <Route path="discovery" element={<AdminDiscovery />} />
                  <Route path="tickets" element={<AdminTickets />} />
                  <Route path="pricing" element={<AdminPricing />} />
                  <Route path="auto-update" element={<AdminAutoUpdate />} />
                </Route>
              </Routes>
              <Toaster position="top-right" richColors />
              <SpinWheel />
              <WhatsAppButton />
            </CartProvider>
          </LanguageProvider>
        </UserProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
